import * as React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { getVirtualProviderLeaguesForSport, getVirtualSportSettings } from '../../Api';
import { VirtualProviderLeagueDto } from '../../models/dtos/VirtualProviderCatalogDto';

/**
 * Searchable league picker backed by the provider.
 *
 * A sport can hold at most one league per daily scheduling slot, so the full list is small enough to
 * load once and filter in the browser rather than round-tripping on every keystroke. With no
 * `sportId` the leagues of every supported sport are offered.
 */
export default function VirtualLeagueSearchSelect(props: {
    value: VirtualProviderLeagueDto | null;
    onChange: (league: VirtualProviderLeagueDto | null) => void;
    sportId?: number | null;
    label?: string;
    disabled?: boolean;
}) {
    const [options, setOptions] = React.useState<VirtualProviderLeagueDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const sportId = props.sportId;

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const load = sportId != null
            ? getVirtualProviderLeaguesForSport(sportId)
            : getVirtualSportSettings().then(sports => Promise
                .all(sports.map(sport => getVirtualProviderLeaguesForSport(sport.id)))
                .then(perSport => perSport.flat()));
        load
            .then(leagues => {
                if (cancelled) return;
                setOptions([...leagues].sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => { if (!cancelled) setOptions([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [sportId]);

    return (
        <Autocomplete
            value={props.value}
            options={options}
            loading={loading}
            disabled={props.disabled}
            onChange={(_, league) => props.onChange(league)}
            getOptionLabel={league => league.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
                <TextField
                    {...params}
                    label={props.label ?? 'League'}
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
